/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard 
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

// Material Dashboard React components
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import CheckCircleTwoToneIcon from "@mui/icons-material/CheckCircleTwoTone";
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import { IconButton, Tooltip } from "@mui/material";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// Permissions
import { subject } from "@casl/ability";
import { useAbility } from "@casl/react";
import { AbilityContext, Can } from "Can";
import { AuthContext } from "context";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// Data
import changeLogService from "services/change-log-service";
import partCodesService from "services/part-code-service";

// E4A Components 
import formatPartCode from "helper/formatPartCode";
import RejectDialog from "./rejectDialog";

function ApprovalPage() {

  // Data
  const [parts, setParts] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  // Permissions
  const ability = useAbility(AbilityContext);
  const authContext = useContext(AuthContext);

  // Fetch part codes
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const response = await partCodesService.getParts();
      response.data.attributes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setParts(response.data.attributes);
    })();
  }, []);


  useEffect(() => {
    setTableData(getRows(parts));
  }, [parts]);

  useEffect(() => {
    setIsVisible(true);
    setTimeout(() => setFooterVisible(true), 100);
  }, []);


  // Format data for DataTable
  const getRows = (info) => {
    let updatedInfo = info
      .filter(e => e.status === "Pending Approval" || e.status === "Rejected")
      .map((e) => {
        return {
          categoryCode: formatPartCode(e),
          id: e._id,
          parentCategory: e.partCategory.parentCategory,
          partCategory: e.partCategory.description,
          description: e.has_attributes_filled ? description(e) : e.description,
          status: e.status,
          statusChip: <Chip label={e.status} color={statusColor(e.status)} variant="outlined" />,
        };
      });
    return updatedInfo
  };

  const statusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Pending Approval":
        return "warning";
      case "Rejected":
        return "error";
    }
  }

  // Helper function to generate the detailed description based on the attributes of the partcode
  const description = (partCode) => {
    const categoryCode = partCode.partCategory.categoryCode;
    return partCode.description.startsWith(categoryCode)
      ? partCode.description
      : `${categoryCode} ${partCode.description}`;
  }

  // Array containing the dataTable's headers and rows
  const dataTableData = {
    columns: [
      { Header: "Part Code", accessor: "categoryCode", width: "20%" },
      { Header: "Part Category", accessor: "partCategory", width: "12%" },
      { Header: "Parent Category", accessor: "parentCategory", width: "13%" },
      { Header: "Description", accessor: "description", width: "25%" },
      {
        Header: "Status",
        accessor: "status",
        width: "15%",
        Cell: ({ row }) => row.original.statusChip,
        type: 'singleSelect',
        valueOptions: ['Rejected', 'Pending Approval'],
      },
      {
        Header: "Actions",
        disableSortBy: true,
        accessor: "actions",
        width: "15%",
        Cell: (info) => {
          return (
            <MDBox display="flex" alignItems="center">
              {/*Actions for partcodes awaiting approval*/}
              {info.cell.row.original.status === "Pending Approval" && (
                <>
                  <Can I="access" this={"approval-page"}>
                    <Tooltip
                      title="Reject Part Code"
                      onClick={() => openRejectDialogHandler(info.cell.row.original.id)}
                    >
                      <IconButton>
                        <CancelTwoToneIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Can>
                  <Can I="access" this={"approval-page"}>
                    <Tooltip
                      title="Approve Part Code"
                      onClick={() => clickApproveHandler(info.cell.row.original.id)}
                    >
                      <IconButton>
                        <CheckCircleTwoToneIcon color="success" />
                      </IconButton>
                    </Tooltip>
                  </Can>
                  <Tooltip
                    title="View Part"
                    onClick={() => navigate(`/part-code-management/view-part-code/${info.cell.row.original.id}`, { state: { approveMode: true } })}
                  >
                    <IconButton>
                      <VisibilityTwoToneIcon color="info" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              {/*Actions for rejected partcodes*/}
              {info.cell.row.original.status === "Rejected" && (
                <>
                  <Can I="Delete" this={subject("PartCode", info.cell.row.original)}>
                    <Tooltip
                      title="Delete Part"
                      onClick={(e) => clickDeleteHandler(e, info.cell.row.original.id)}
                    >
                      <IconButton>
                        <DeleteTwoToneIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Can>
                  <Can I="access" this={"edit-part-code"}>
                    <Tooltip
                      title="Edit Part"
                      onClick={() => navigate(`/part-code-management/edit-part-code/${info.cell.row.original.id}`)}
                    >
                      <IconButton>
                        <EditTwoToneIcon color="success" />
                      </IconButton>
                    </Tooltip>
                  </Can>
                  <Tooltip
                    title="View Part"
                    onClick={() => navigate(`/part-code-management/view-part-code/${info.cell.row.original.id}`, { state: { approveMode: false } })}
                  >
                    <IconButton>
                      <VisibilityTwoToneIcon color="info" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </MDBox>
          );
        },
      },
    ],
    rows: tableData
  };

  useEffect(() => {
    if (isLoading && tableData.length > 0) {
      setIsLoading(false);
    }
  }, [tableData])

  const reloadDataTable = async () => {
    setIsLoading(true);
    const response = await partCodesService.getParts();
    response.data.attributes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    setParts(response.data.attributes);
  }

  // Behaviour when clicking the edit part button
  const clickApproveHandler = async (id) => {
    try {
      if (!confirm("Are you sure you want to approve this part?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {

        await partCodesService.approvePart(id);

        const changeLog = {
          data: {
            attributes: {
              partCode: id,
              change: {
                type: "Approved",
                description: "Part Code Approved",
                date: new Date(),
                user: await authContext.getCurrentUser()
              }
            }
          }
        }

        await changeLogService.addChange(changeLog);

        // Reload the dataTable
        reloadDataTable(id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Behaviour when clicking the delete part button
  const clickDeleteHandler = async (e, id) => {
    try {
      if (!confirm("Are you sure you want to delete this part?")) {
        e.nativeEvent.stopImmediatePropagation();
      } else {
        const userId = await authContext.getCurrentUser();
        await partCodesService.deletePart(id, userId);
        // Reload the dataTable
        const response = await partCodesService.getParts();
        setParts(response.data.attributes);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handler to open the reject dialog
  const openRejectDialogHandler = (id) => {
    setSelectedPartId(id);
    setOpenRejectDialog(true);
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* Reject Dialog */}
      <RejectDialog
        open={openRejectDialog}
        handleClose={() => setOpenRejectDialog(false)}
        reloadDataTable={reloadDataTable}
        id={selectedPartId}
      />

      <MDBox pt={6} pb={3}>

        {/* Main Card */}
        <Card>

          {/* Title and Create Button */}
          <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="medium">
              Parts Codes Awaiting Approval
            </MDTypography>
          </MDBox>

          {/* Data Table */}
          <MDBox>
            <DataTable table={dataTableData} canSearch isLoading={isLoading} tableId="parts-approval-table" />
          </MDBox>

        </Card>
      </MDBox>

      <MDBox>

        <Footer />

      </MDBox>

    </DashboardLayout>
  );
}

export default ApprovalPage;
